// Small DOM helpers. Text is always set with textContent, never innerHTML (CODING-RULES §4.3).

/** A new element with an optional class and text. */
export function createElement<K extends keyof HTMLElementTagNameMap>(tag: K, className = "",
  text = ""): HTMLElementTagNameMap[K] {
  const element: HTMLElementTagNameMap[K] = document.createElement(tag);
  if (className !== "") {
    element.className = className;
  }

  if (text !== "") {
    element.textContent = text;
  }

  return element;
}

/** Appends children, skipping nulls (optional regions). */
export function appendChildren(parent: HTMLElement, ...children: (Node | null)[]): HTMLElement {
  for (const child of children) {
    if (child !== null) {
      parent.append(child);
    }
  }

  return parent;
}

/** A link inside the app, e.g. "#/groups/123". */
export function createLink(route: string, text: string, className = ""): HTMLAnchorElement {
  const link: HTMLAnchorElement = createElement("a", className, text);
  link.href = "#" + route;
  return link;
}

/** An external link (tel:, mailto:, https:). */
export function createExternalLink(href: string, text: string): HTMLAnchorElement {
  const link: HTMLAnchorElement = createElement("a", "", text);
  link.href = href;
  if (href.startsWith("https:")) {
    link.target = "_blank";
    link.rel = "noopener noreferrer";
  }

  return link;
}

/**
 * A button. While an async click handler runs, the button is disabled and shows progress (SPEC §9.4).
 */
export function createButton(label: string, onClick: () => void | Promise<void>, className = ""): HTMLButtonElement {
  const button: HTMLButtonElement = createElement("button", className === "" ? "button" : `button ${className}`,
    label);
  button.type = "button";
  button.addEventListener("click", (): void => {
    void runWithProgress(button, onClick);
  });
  return button;
}

/** A heading of the given level. */
export function createHeading(level: 1 | 2 | 3, text: string): HTMLHeadingElement {
  return createElement(`h${level}`, "", text);
}

/** A small rectangular status label (SPEC §9.2: badges are rectangles, never colour alone). */
export function createBadge(text: string, kind: string): HTMLSpanElement {
  return createElement("span", `badge badge-${kind}`, text);
}

/** Removes every child. */
export function clearElement(element: HTMLElement): void {
  element.replaceChildren();
}

async function runWithProgress(button: HTMLButtonElement, onClick: () => void | Promise<void>): Promise<void> {
  if (button.disabled) {
    return;
  }

  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  try {
    await onClick();
  } finally {
    button.disabled = false;
    button.removeAttribute("aria-busy");
  }
}
